// Label design registry. A template decides only what is drawn INSIDE one label
// card; the sheet (paper, grid, margins, gaps, pagination, calibration) and the
// barcode pipeline are shared and never depend on the template.
//
// To add a design: create its module next to footwearClassic.js and add one
// entry to LABEL_TEMPLATES.
//   renderCard(label, barcodeSvg, { attributes, className }) -> card markup
//   cardCss                                                  -> card CSS
//   getBarcodeDrawableWidthMM(labelWidthMM, geometry, content)
//       width the bars are stretched across; the density check uses it, so a
//       template that draws the barcode differently must say how wide it is.
//   validateFit(geometry, content) -> array of issue codes

import {
  getBarcodeDrawableWidthMM,
  validateLabelContentFit,
} from "../barcodeLabelTemplate";
import { footwearClassic } from "./footwearClassic";
import { textileModern } from "./textileModern";

export const DEFAULT_LABEL_TEMPLATE_ID = footwearClassic.id;

export const LABEL_TEMPLATES = Object.freeze({
  [footwearClassic.id]: Object.freeze({
    ...footwearClassic,
    getBarcodeDrawableWidthMM,
    validateFit: validateLabelContentFit,
  }),
  [textileModern.id]: textileModern,
});

export const LABEL_TEMPLATE_IDS = Object.freeze(Object.keys(LABEL_TEMPLATES));

export class UnknownLabelTemplateError extends Error {
  constructor(templateId) {
    super(`Unknown label template "${templateId}".`);
    this.name = "UnknownLabelTemplateError";
    this.templateId = templateId;
  }
}

export const isLabelTemplateId = (templateId) =>
  typeof templateId === "string" && Object.prototype.hasOwnProperty.call(LABEL_TEMPLATES, templateId);

/**
 * Omitted (undefined / null) selects the default footwear design, which is what
 * every pre-template caller printed. Any other unknown id throws: a label is
 * never printed with a design the caller did not ask for.
 */
export const resolveLabelTemplate = (templateId) => {
  if (templateId == null) return LABEL_TEMPLATES[DEFAULT_LABEL_TEMPLATE_ID];
  if (!isLabelTemplateId(templateId)) throw new UnknownLabelTemplateError(templateId);
  return LABEL_TEMPLATES[templateId];
};
